// Wise PDF activity statement loader. Mirrors scripts/loaders.py::load_wise_pdf.
//
// PDF source: Wise's downloadable Transaction history PDF. Embedded
// "This is not an official statement." watermark.
//
// Algorithm:
//   1. Reconstruct the PDF to layout-text (PDF.js item-coordinates path).
//   2. Strip leading whitespace from each line; filter junk (the
//      watermark, page footers, month-header rows).
//   3. Walk lines looking for date anchors of the form
//        `<day> <Month> <Year> | Transaction: TRANSFER-<id> ...`
//      and emit one Transaction per anchor by pairing it with the
//      preceding vendor line (≤3-line look-back).
//   4. Sign convention: a leading `Sent |` marker means outflow → expense
//      (positive in our model). Bare `|` means inflow → income (negative).
//      A category containing "sent" overrides if the `Sent` marker is
//      absent (rare).
//   5. Multi-currency: native currency from the vendor line, no FX
//      conversion. Account label `Wise (USD)` / `Wise (CAD)` / etc.
//
// Notes vs Python loader:
//   - Python calls `pdftotext -raw`; we use the project's PDF.js
//     reconstruction (`pdfToTextLayout`). Output is semantically the
//     same modulo leading column-pad whitespace, which we strip up front.
//   - Real PDFs occasionally wrap the date line across two lines (a
//     "Reference:" tail spills onto the next line, with the Category
//     marker on the continuation). We merge a peek-ahead non-anchor line
//     into the date line before extracting Category.

import {
  type Transaction,
  newTransaction,
  makeId,
  parseAmount,
  normalizeVendor,
  isInterPortalTransfer,
} from "../common.js";
import { pdfToTextLayout } from "../pdf.js";

const MONTHS_LONG = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

const JUNK_PATTERNS: RegExp[] = [
  /^This is not an official statement/i,
  // Truncated variants — sometimes only part of the watermark survives
  // when it overlaps reconstructed-column whitespace.
  /^This is not an official statemen$/i,
  /^\d+\s*\/\s*\d+\s*$/, // page x/y
  /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}\s+Fee\s+Amount\s*$/i,
];

const DATE_LINE_RE = new RegExp(
  String.raw`^\s*(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})\s*\|\s*Transaction:\s*(\S+)`,
  "i",
);

// Vendor line: `[Sent ]| <vendor>  <a1> <CCY> [<a2> <CCY>]`. Trailing
// pair (a2) is the gross amount when present; a1 is then the fee. With
// only a1, that's the amount.
const VENDOR_LINE_RE =
  /^(Sent\s+)?\|\s+(.+?)\s+(-?[\d,]+(?:\.\d+)?)\s+([A-Z]{3})(?:\s+(-?[\d,]+(?:\.\d+)?)\s+([A-Z]{3}))?\s*$/;

function isJunk(line: string): boolean {
  if (!line) return true;
  for (const p of JUNK_PATTERNS) if (p.test(line)) return true;
  return false;
}

export async function loadWisePdf(
  data: ArrayBuffer | Uint8Array,
  filename: string,
): Promise<Transaction[]> {
  const text = await pdfToTextLayout(data);
  const lines = text.split("\n").map((l) => l.trim()).filter((l) => !isJunk(l));

  const out: Transaction[] = [];
  for (let i = 0; i < lines.length; i++) {
    const dm = DATE_LINE_RE.exec(lines[i]);
    if (!dm) continue;

    const day = parseInt(dm[1], 10);
    const monthIdx = MONTHS_LONG.indexOf(dm[2].toLowerCase());
    const year = parseInt(dm[3], 10);
    const txId = dm[4];
    if (monthIdx < 0 || !Number.isFinite(day) || !Number.isFinite(year)) continue;
    const date = `${year.toString().padStart(4, "0")}-${(monthIdx + 1).toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;

    // Look back ≤3 lines for the vendor row. The order in the PDF is
    // vendor-then-date, so the immediately-prior line is usually it.
    let vm: RegExpExecArray | null = null;
    for (let j = i - 1; j >= Math.max(0, i - 3); j--) {
      vm = VENDOR_LINE_RE.exec(lines[j]);
      if (vm) break;
    }
    if (!vm) continue;

    let isOutflow = vm[1] !== undefined; // "Sent " group matched
    const vendor = vm[2].trim();
    if (!vendor) continue;

    // Prefer the second amount/currency pair (gross) when present, else
    // the only one. The first amount is the fee for two-pair rows.
    let amountStr: string;
    let currency: string;
    if (vm[5] !== undefined && vm[6] !== undefined) {
      amountStr = vm[5];
      currency = vm[6];
    } else {
      amountStr = vm[3];
      currency = vm[4];
    }
    const amount = parseAmount(amountStr);

    // Category lives on the date line; may wrap to the next line on
    // "Reference: ..." rows. Peek ahead and merge a single non-date,
    // non-vendor continuation into the search corpus.
    let dateLineCombined = lines[i];
    const nextLine = lines[i + 1];
    if (
      nextLine !== undefined &&
      !DATE_LINE_RE.test(nextLine) &&
      !VENDOR_LINE_RE.test(nextLine)
    ) {
      dateLineCombined = `${dateLineCombined} ${nextLine}`;
    }
    let category = "";
    const catMatch = /Category:\s*([A-Za-z][A-Za-z\s/&'-]*)/i.exec(dateLineCombined);
    if (catMatch) {
      category = catMatch[1].trim();
      // Rare: no `Sent ` marker but Category says "Sent ..." — treat as
      // outflow.
      if (!isOutflow && /sent/i.test(category)) {
        isOutflow = true;
      }
    }

    const signed = isOutflow ? amount : -amount;
    const description = category ? `${vendor} (${category})` : vendor;
    if (isInterPortalTransfer(description)) continue;

    const account = `Wise (${currency})`;
    const id = await makeId("wise", account, date, signed, description);
    out.push(
      newTransaction({
        id,
        date,
        vendor_raw: description,
        vendor_key: normalizeVendor(vendor) || "wise",
        amount: Math.round(signed * 100) / 100,
        portal: "wise",
        account,
        account_kind: "debit",
        source_file: filename,
        raw: {
          counterparty: vendor,
          transaction_id: txId,
          category,
          currency,
          type: isOutflow ? "sent" : "received",
        },
      }),
    );
  }
  return out;
}

export const _internal = { isJunk, DATE_LINE_RE, VENDOR_LINE_RE, JUNK_PATTERNS };
